import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.jsx';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Users, 
  FileText, 
  Calendar, 
  Send,
  Mail,
  CheckCircle,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const STEPS = [
  { id: 1, title: 'Empfänger', icon: Users },
  { id: 2, title: 'Template', icon: FileText },
  { id: 3, title: 'Zeitplanung', icon: Calendar },
  { id: 4, title: 'Test', icon: Mail },
  { id: 5, title: 'Bestätigung', icon: CheckCircle },
];

const SendNewsletterWizard = ({ open, onClose, templates, onSuccess }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [actionStats, setActionStats] = useState([]);
  const [totalUniqueSubscribers, setTotalUniqueSubscribers] = useState(0);
  const [countsLoading, setCountsLoading] = useState(false);
  const [previewCount, setPreviewCount] = useState(null); // Deduplicated count for selected actions
  const [redeemCounts, setRedeemCounts] = useState({ all: null, redeemed: null, open: null }); // Counts for each redeem status

  const [formData, setFormData] = useState({
    recipientType: 'all', // 'all' or 'actions'
    selectedActionIds: [],
    redeemStatus: 'all', // 'all', 'redeemed', or 'open'
    templateId: '',
    subject: '',
    scheduledFor: '', // ISO date string or empty for immediate
    testEmail: '',
  });

  useEffect(() => {
    if (open && token) {
      loadActions();
    }
  }, [open, token]);

  // Load deduplicated preview count when actions selection changes
  useEffect(() => {
    if (formData.recipientType === 'actions' && formData.selectedActionIds.length > 0 && token) {
      loadRedeemCounts(); // Load counts for all redeem statuses (deduplicated)
    } else {
      setPreviewCount(null);
      setRedeemCounts({ all: null, redeemed: null, open: null });
    }
  }, [formData.recipientType, formData.selectedActionIds.join(','), token]);

  const loadPreviewCount = async () => {
    try {
      const recipientFilter = { 
        actionIds: formData.selectedActionIds,
        redeemStatus: formData.redeemStatus
      };
      const result = await api.newslettersSubscribersPreviewCount(token, recipientFilter);
      setPreviewCount(result.uniqueRecipients);
    } catch (error) {
      console.error('Error loading preview count:', error);
      setPreviewCount(null);
    }
  };

  const loadRedeemCounts = async () => {
    try {
      // Load counts for all three redeem statuses in parallel
      const [allResult, redeemedResult, openResult] = await Promise.all([
        api.newslettersSubscribersPreviewCount(token, { 
          actionIds: formData.selectedActionIds,
          redeemStatus: 'all'
        }),
        api.newslettersSubscribersPreviewCount(token, { 
          actionIds: formData.selectedActionIds,
          redeemStatus: 'redeemed'
        }),
        api.newslettersSubscribersPreviewCount(token, { 
          actionIds: formData.selectedActionIds,
          redeemStatus: 'open'
        }),
      ]);
      
      setRedeemCounts({
        all: allResult.uniqueRecipients,
        redeemed: redeemedResult.uniqueRecipients,
        open: openResult.uniqueRecipients,
      });
    } catch (error) {
      console.error('Error loading redeem counts:', error);
      setRedeemCounts({ all: null, redeemed: null, open: null });
    }
  };

  const loadActions = async () => {
    try {
      setCountsLoading(true);
      const [actionsData, statsData, totalData] = await Promise.all([
        api.listActions(token),
        api.newslettersSubscribersByAction(token),
        api.newslettersSubscriberCount(token),
      ]);
      console.debug('[SendWizard] actions:', actionsData?.length, 'byAction:', statsData, 'total:', totalData);
      setActions(actionsData);
      setActionStats(statsData);
      setTotalUniqueSubscribers(totalData?.totalUniqueSubscribers || 0);
    } catch (error) {
      console.error('Error loading actions:', error);
    } finally {
      setCountsLoading(false);
    }
  };

  const handleNext = () => {
    // Validation per step
    if (currentStep === 1) {
      if (formData.recipientType === 'actions' && formData.selectedActionIds.length === 0) {
        toast({
          title: t('common.error'),
          description: t('common.alerts.pleaseSelectAction'),
          variant: 'destructive',
        });
        return;
      }
    }

    if (currentStep === 2) {
      if (!formData.templateId || !formData.subject) {
        toast({
          title: t('common.error'),
          description: t('common.alerts.pleaseSelectTemplateAndSubject'),
          variant: 'destructive',
        });
        return;
      }
    }

    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSendTest = async () => {
    if (!formData.testEmail) {
      toast({
        title: 'Fehler',
        description: 'Bitte geben Sie eine Test-Email-Adresse ein.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      await api.newslettersSendTest(token, {
        templateId: formData.templateId,
        subject: formData.subject,
        testEmail: formData.testEmail,
      });

      toast({
        title: 'Erfolg',
        description: 'Test-Email wurde versendet.',
      });
    } catch (error) {
      console.error('Error sending test:', error);
      toast({
        title: 'Fehler',
        description: 'Test-Email konnte nicht versendet werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      setLoading(true);

      const recipientFilter = formData.recipientType === 'all'
        ? { all: true }
        : { 
            actionIds: formData.selectedActionIds,
            redeemStatus: formData.redeemStatus
          };

      // Convert datetime-local value (which is in local German time) to UTC ISO string
      let scheduledForUTC = null;
      if (formData.scheduledFor) {
        // datetime-local format: "2025-12-24T15:30" (local time)
        // We need to convert it to UTC ISO string
        const localDate = new Date(formData.scheduledFor);
        scheduledForUTC = localDate.toISOString();
      }

      await api.newslettersSend(token, {
        templateId: formData.templateId,
        subject: formData.subject,
        recipientFilter: JSON.stringify(recipientFilter),
        scheduledFor: scheduledForUTC,
      });

      onSuccess();
    } catch (error) {
      console.error('Error sending newsletter:', error);
      toast({
        title: 'Fehler',
        description: 'Newsletter konnte nicht versendet werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getTotalRecipients = () => {
    if (formData.recipientType === 'all') {
      return totalUniqueSubscribers;
    }
    // For actions: ALWAYS use deduplicated counts to avoid counting users multiple times
    // If we have redeem counts, use the appropriate one (this is the deduplicated count)
    if (redeemCounts[formData.redeemStatus] !== null) {
      return redeemCounts[formData.redeemStatus];
    }
    // If we have a deduplicated preview count, use it
    if (previewCount !== null) {
      return previewCount;
    }
    // If counts are still loading, show loading indicator
    return '…';
  };

  const selectedTemplate = templates.find(t => t.id === formData.templateId);

  // Helper: check if selected template includes a claimButton without explicit actionId
  function hasClaimButtonWithoutActionId(template) {
    if (!template) return false;
    let json = template.publishedJson || template.draftJson;
    if (!json) return false;
    try {
      if (typeof json === 'string') json = JSON.parse(json);
    } catch {}
    let found = false;
    let foundWithActionId = false;
    const visit = (node) => {
      if (!node) return;
      if (node.type === 'claimButton') {
        found = true;
        if (node.props && node.props.actionId) foundWithActionId = true;
      }
      if (Array.isArray(node.children)) node.children.forEach(visit);
    };
    if (json.root) visit(json.root);
    if (Array.isArray(json.blocks)) json.blocks.forEach(visit);
    return found && !foundWithActionId;
  }

  // Helper: check if selected template includes an emailQr block
  function hasEmailQr(template) {
    if (!template) return false;
    let json = template.publishedJson || template.draftJson;
    if (!json) return false;
    try {
      if (typeof json === 'string') json = JSON.parse(json);
    } catch {}
    let found = false;
    const visit = (node) => {
      if (!node) return;
      if (node.type === 'emailQr') found = true;
      if (Array.isArray(node.children)) node.children.forEach(visit);
    };
    if (json.root) visit(json.root);
    if (Array.isArray(json.blocks)) json.blocks.forEach(visit);
    return found;
  }

  const showClaimWarning = formData.recipientType === 'all' && hasClaimButtonWithoutActionId(selectedTemplate);
  const showQrWarning = formData.recipientType === 'all' && hasEmailQr(selectedTemplate);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Newsletter senden
          </DialogTitle>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-6 px-4">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isActive
                        ? 'bg-blue-500 text-white'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-xs mt-1 font-medium">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className="flex-1 h-0.5 bg-muted mx-2" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="min-h-[300px]">
          {/* Step 1: Recipient Selection */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Empfänger auswählen</h3>
              
              <div className="space-y-3">
                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-muted">
                  <input
                    type="radio"
                    name="recipientType"
                    value="all"
                    checked={formData.recipientType === 'all'}
                    onChange={(e) => setFormData({ ...formData, recipientType: e.target.value })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Alle Abonnenten</div>
                    <div className="text-sm text-muted-foreground">
                      Newsletter an alle aktiven Abonnenten senden
                      ({countsLoading ? '…' : totalUniqueSubscribers} Empfänger)
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-muted">
                  <input
                    type="radio"
                    name="recipientType"
                    value="actions"
                    checked={formData.recipientType === 'actions'}
                    onChange={(e) => setFormData({ ...formData, recipientType: e.target.value })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Spezifische Aktionen</div>
                    <div className="text-sm text-muted-foreground mb-3">
                      Nur Abonnenten ausgewählter Aktionen
                    </div>

                    {formData.recipientType === 'actions' && (
                      <div className="space-y-2 mt-3 max-h-60 overflow-y-auto border-t pt-3">
                        {countsLoading ? (
                          <div className="text-sm text-muted-foreground text-center py-4">Laden...</div>
                        ) : actionStats.length === 0 ? (
                          <div className="text-sm text-muted-foreground text-center py-4">
                            Keine Aktionen gefunden
                          </div>
                        ) : (
                          actionStats.map((stat) => (
                            <label key={stat.actionId} className="flex items-center gap-2 p-2 hover:bg-muted rounded">
                              <input
                                type="checkbox"
                                checked={formData.selectedActionIds.includes(stat.actionId)}
                                onChange={(e) => {
                                  const ids = e.target.checked
                                    ? [...formData.selectedActionIds, stat.actionId]
                                    : formData.selectedActionIds.filter(id => id !== stat.actionId);
                                  setFormData({ ...formData, selectedActionIds: ids });
                                }}
                              />
                              <span className="flex-1 text-sm">{stat.actionTitle}</span>
                              <span className="text-xs text-muted-foreground">
                                {stat.subscriberCount} Abonnenten
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* Redeem Status Filter - nur für Aktionen */}
              {formData.recipientType === 'actions' && formData.selectedActionIds.length > 0 && (
                <div className="space-y-3 mt-4">
                  <Label className="font-medium">Code-Einlöse-Status</Label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted">
                      <input
                        type="radio"
                        name="redeemStatus"
                        value="all"
                        checked={formData.redeemStatus === 'all'}
                        onChange={(e) => setFormData({ ...formData, redeemStatus: e.target.value })}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">Alle</div>
                        <div className="text-xs text-muted-foreground">Alle Abonnenten der ausgewählten Aktionen</div>
                      </div>
                      {redeemCounts.all !== null && (
                        <span className="text-sm font-semibold text-foreground bg-muted px-3 py-1 rounded-full">
                          {redeemCounts.all}
                        </span>
                      )}
                    </label>

                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted">
                      <input
                        type="radio"
                        name="redeemStatus"
                        value="redeemed"
                        checked={formData.redeemStatus === 'redeemed'}
                        onChange={(e) => setFormData({ ...formData, redeemStatus: e.target.value })}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">Eingelöst</div>
                        <div className="text-xs text-muted-foreground">Nur Abonnenten, die den Code bereits eingelöst haben</div>
                      </div>
                      {redeemCounts.redeemed !== null && (
                        <span className="text-sm font-semibold text-green-700 bg-green-100 px-3 py-1 rounded-full">
                          {redeemCounts.redeemed}
                        </span>
                      )}
                    </label>

                    <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted">
                      <input
                        type="radio"
                        name="redeemStatus"
                        value="open"
                        checked={formData.redeemStatus === 'open'}
                        onChange={(e) => setFormData({ ...formData, redeemStatus: e.target.value })}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-sm">Offen</div>
                        <div className="text-xs text-muted-foreground">Nur Abonnenten, die den Code noch nicht eingelöst haben</div>
                      </div>
                      {redeemCounts.open !== null && (
                        <span className="text-sm font-semibold text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
                          {redeemCounts.open}
                        </span>
                      )}
                    </label>
                  </div>
                </div>
              )}

              <Card className="p-4 bg-blue-50 border-blue-200">
                <p className="text-sm font-medium">
                  Gesamt: <strong>{countsLoading ? '…' : getTotalRecipients()}</strong> eindeutige Empfänger
                </p>
                {formData.recipientType === 'actions' && formData.selectedActionIds.length > 1 && previewCount !== null && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Duplikate wurden automatisch entfernt – Nutzer, die in mehreren Aktionen vorkommen, erhalten nur eine E-Mail.
                  </p>
                )}
              </Card>
            </div>
          )}

          {/* Step 2: Template Selection */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Template & Betreff</h3>

              <div>
                <Label htmlFor="template">Newsletter-Template</Label>
                <Select
                  value={formData.templateId}
                  onValueChange={(value) => {
                    const template = templates.find(t => t.id === value);
                    setFormData({
                      ...formData,
                      templateId: value,
                      subject: template?.subject || formData.subject,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('common.form.chooseTemplate')} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name} {template.status === 'PUBLISHED' && '✓'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="subject">Betreff *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Newsletter-Betreff"
                />
              </div>

              {selectedTemplate && (
                <Card className="p-4 bg-muted">
                  <h4 className="font-medium text-sm mb-2">Template-Vorschau</h4>
                  <p className="text-sm text-muted-foreground">{selectedTemplate.subject}</p>
                  {selectedTemplate.previewText && (
                    <p className="text-xs text-muted-foreground mt-1">{selectedTemplate.previewText}</p>
                  )}
                </Card>
              )}

              {(showClaimWarning || showQrWarning) && (
                <Card className="p-4 bg-yellow-50 border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    Hinweis: Dieses Template enthält {showClaimWarning && showQrWarning ? 'einen Code-Button und einen QR-Block' : showClaimWarning ? 'einen Code-Button' : 'einen QR-Block'}. Für individuelle Links pro Empfänger wählen Sie bitte eine <strong>Aktion</strong> als Empfängerfilter
                    {showClaimWarning ? ' oder tragen Sie in den Button-Einstellungen eine ' : ' '}<strong>{showClaimWarning ? 'Aktion ID' : 'Aktion'}</strong> ein. Mit "Alle Abonnenten" ohne Aktion bleiben Links unpersonalisiert und QR-Codes können keinen individuellen Link enthalten.
                  </p>
                </Card>
              )}
            </div>
          )}

          {/* Step 3: Scheduling */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Zeitplanung</h3>

              <div className="space-y-3">
                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-muted">
                  <input
                    type="radio"
                    name="scheduling"
                    checked={!formData.scheduledFor}
                    onChange={() => setFormData({ ...formData, scheduledFor: '' })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Sofort senden</div>
                    <div className="text-sm text-muted-foreground">
                      Newsletter wird sofort nach Bestätigung versendet
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-muted">
                  <input
                    type="radio"
                    name="scheduling"
                    checked={!!formData.scheduledFor}
                    onChange={() => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      tomorrow.setHours(10, 0, 0, 0);
                      setFormData({ ...formData, scheduledFor: tomorrow.toISOString().slice(0, 16) });
                    }}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium">Geplant senden</div>
                    <div className="text-sm text-muted-foreground mb-3">
                      Newsletter zu einem bestimmten Zeitpunkt senden
                    </div>

                    {!!formData.scheduledFor && (
                      <Input
                        type="datetime-local"
                        value={formData.scheduledFor}
                        onChange={(e) => setFormData({ ...formData, scheduledFor: e.target.value })}
                        min={new Date().toISOString().slice(0, 16)}
                      />
                    )}
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Step 4: Test Email */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Test-Email senden</h3>
              <p className="text-sm text-muted-foreground">
                Senden Sie eine Test-Email, um das Layout und den Inhalt zu überprüfen.
              </p>

              <div>
                <Label htmlFor="testEmail">Test-Email-Adresse</Label>
                <Input
                  id="testEmail"
                  type="email"
                  value={formData.testEmail}
                  onChange={(e) => setFormData({ ...formData, testEmail: e.target.value })}
                  placeholder="ihre.email@beispiel.de"
                />
              </div>

              <Button
                onClick={handleSendTest}
                disabled={loading || !formData.testEmail}
                variant="outline"
                className="w-full"
              >
                <Mail className="w-4 h-4 mr-2" />
                Test-Email senden
              </Button>

              <Card className="p-4 bg-yellow-50 border-yellow-200">
                <p className="text-sm text-foreground">
                  💡 Die Test-Email wird mit dem Präfix <strong>[TEST]</strong> versendet.
                </p>
              </Card>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Zusammenfassung & Bestätigung</h3>

              <Card className="p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Empfänger:</span>
                  <span className="text-sm">
                    {formData.recipientType === 'all' 
                      ? 'Alle Abonnenten' 
                      : `${formData.selectedActionIds.length} Aktion(en)`}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm font-medium">Anzahl Empfänger:</span>
                  <span className="text-sm font-semibold">{getTotalRecipients()}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm font-medium">Template:</span>
                  <span className="text-sm">{selectedTemplate?.name}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm font-medium">Betreff:</span>
                  <span className="text-sm">{formData.subject}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-sm font-medium">Versand:</span>
                  <span className="text-sm">
                    {formData.scheduledFor 
                      ? `Geplant für ${new Date(formData.scheduledFor).toLocaleString('de-DE')}`
                      : 'Sofort'}
                  </span>
                </div>
              </Card>

              <Card className="p-4 bg-green-50 border-green-200">
                <p className="text-sm text-foreground">
                  ✓ Alles bereit! Der Newsletter wird {formData.scheduledFor ? 'zum geplanten Zeitpunkt' : 'sofort'} versendet.
                </p>
              </Card>

              {(showClaimWarning || showQrWarning) && (
                <Card className="p-4 bg-yellow-50 border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    Hinweis: Template enthält {showClaimWarning && showQrWarning ? 'Code-Button und QR-Block' : showClaimWarning ? 'Code-Button' : 'QR-Block'}, Empfänger sind "Alle Abonnenten". Ohne Aktion/ActionId sind die Links nicht individuell und QR kann keinen persönlichen Link darstellen.
                  </p>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <DialogFooter className="flex justify-between gap-2">
          <div className="flex gap-2">
            {currentStep > 1 && (
              <Button variant="outline" onClick={handleBack}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Zurück
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>

            {currentStep < 5 && (
              <Button onClick={handleNext}>
                Weiter
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}

            {currentStep === 5 && (
              <Button onClick={handleSend} disabled={loading}>
                <Send className="w-4 h-4 mr-2" />
                {formData.scheduledFor ? 'Planen' : 'Jetzt senden'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendNewsletterWizard;
